import { useEffect, useRef } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import type { Thread } from "@/entities/thread/types";
import { isR18ThreadTagName } from "@/entities/thread/utils";
import type { BoardPageEvent } from "@/features/board/boardPageMediator";
import type { BoardThreadTail } from "@/features/board/hooks/useBoardThreads";
import { useBoardPagePresenter } from "@/features/board/useBoardPagePresenter";
import {
  type BoardPostProps,
  useBoardPostPresenter,
} from "@/features/board/useBoardPostPresenter";
import { CatalogSortModal } from "@/features/catalog/components/modals/CatalogSortModal";
import { TagFilterModal } from "@/features/catalog/components/modals/TagFilterModal";
import { CatalogPagination } from "@/features/catalog/components/navigation/CatalogPagination";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { DesktopCatalogToolbar } from "@/features/catalog/components/navigation/DesktopCatalogToolbar";
import { MobileCatalogFooter } from "@/features/catalog/components/navigation/MobileCatalogFooter";
import { getThreadLinkAttributes } from "@/features/catalog/hooks/useFilteredThreads";
import { DesktopHistorySidebar } from "@/features/history/components/DesktopHistorySidebar";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { DesktopThreadCreatePanel } from "@/features/thread/components/desktop/DesktopThreadCreatePanel";
import { ThreadCreateModal } from "@/features/thread/components/modals/ThreadCreateModal";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import { Message, RefreshStatus } from "@/shared/ui/feedback";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";

interface BoardQueryStateProps {
  children: React.ReactNode;
  error: unknown;
  hasData: boolean;
  isLoading: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}

export const BoardQueryState: React.FunctionComponent<BoardQueryStateProps> = ({
  children,
  error,
  hasData,
  isLoading,
  isRetrying,
  onRetry,
}: BoardQueryStateProps) => (
  <QueryStateBoundary
    error={error}
    hasData={hasData}
    isLoading={isLoading}
    isRetrying={isRetrying}
    message="スレッド一覧の読み込みに失敗しました"
    onRetry={onRetry}
  >
    {children}
  </QueryStateBoundary>
);

export function BoardPage(): React.ReactNode {
  const { node, ...view } = useBoardPagePresenter();
  return (
    <ComponentScope node={node}>
      <BoardPageView
        {...view}
        onEvent={(event) => node.dispatch(event).status === "handled"}
      />
    </ComponentScope>
  );
}
type BoardPageViewProps = Omit<
  ReturnType<typeof useBoardPagePresenter>,
  "node"
> & { onEvent: (event: BoardPageEvent) => boolean };
export function BoardPageView({
  isDesktop,
  isSettingsOpen,
  isSortOpen,
  isTagFilterOpen,
  currentSort,
  autoReload,
  openInNewTab,
  isThreadCreateOpen,
  showR18,
  desktopThreadCreateButtonRef,
  desktopHistorySidebarRef,
  page,
  data,
  isLoading,
  isFetching,
  error,
  isManualRefreshing,
  threads,
  tails,
  openThreadCreateOnFileDrag,
  onEvent,
}: BoardPageViewProps): React.ReactNode {
  const pagination = (
    <CatalogPagination
      page={page}
      hasNextPage={data?.pagination.hasNextPage ?? false}
      isFetching={isFetching}
      onChange={(page) => onEvent({ type: "catalog/page-selected", page })}
      ariaLabel="リストのページ移動"
    />
  );

  const threadList = (
    <BoardThreadList
      threads={threads}
      tails={tails.tails}
      tailsIsFetching={tails.isFetching}
      tailsHasError={tails.hasError}
      openInNewTab={openInNewTab}
      showR18={showR18}
    />
  );

  if (isDesktop) {
    return (
      <section
        aria-labelledby="board-page-title"
        className="desktop-catalog-page"
      >
        <h1 id="board-page-title" className="sr-only">
          リストモード
        </h1>
        <BoardQueryState
          error={error}
          hasData={data !== undefined}
          isLoading={isLoading}
          isRetrying={isFetching}
          onRetry={(): void => {
            onEvent({ type: "catalog/retry-requested" });
          }}
        >
          <DesktopCatalogToolbar
            activeMode="board"
            tagFilterThreads={data?.threads ?? []}
            currentSort={currentSort}
            selectSortMethod={(sort) =>
              onEvent({ type: "catalog/sort-selected", sort })
            }
            autoReload={autoReload}
            setAutoReload={(enabled) =>
              onEvent({ type: "catalog/auto-reload-changed", enabled })
            }
            isFetching={isFetching}
            onRefresh={(): void => {
              onEvent({ type: "catalog/refresh-requested", manual: true });
            }}
            onToggleSidebar={(): void => {
              onEvent({ type: "catalog/sidebar-toggle-requested" });
            }}
            onScrollToTop={(): void => {
              onEvent({ type: "catalog/scroll-requested", edge: "top" });
            }}
            onScrollToBottom={(): void => {
              onEvent({ type: "catalog/scroll-requested", edge: "bottom" });
            }}
            onSettingsClick={(
              event: React.MouseEvent<HTMLAnchorElement>,
            ): void => {
              if (
                onEvent({
                  type: "catalog/settings-link-clicked",
                  hasModifier: hasLinkModifierKey(event),
                })
              )
                event.preventDefault();
            }}
            onOpenThreadCreate={() =>
              onEvent({ type: "catalog/thread-create-open-requested" })
            }
          />
          <RefreshStatus isRefreshing={isManualRefreshing} />
          <p aria-hidden="true" className="desktop-mode-title">
            リストモード
          </p>
          {/*
          CatalogPageのPC版構造(desktop-catalog-grid-wrap)に合わせ、
          board-page(width/padding付き)をここで使わない。desktop-catalog-page
          に board-page をネストするとpaddingが二重にかかり幅も不一致になる
          ため(#42レビュー指摘)。
        */}
          <section
            className="desktop-catalog-grid-wrap"
            aria-label="スレッド一覧"
          >
            {threadList}
          </section>
          {pagination}
          <DesktopHistorySidebar
            ref={desktopHistorySidebarRef}
            showClosedHandle={false}
          />
          <DesktopThreadCreatePanel
            isOpen={isThreadCreateOpen}
            onClose={() =>
              onEvent({ type: "catalog/thread-create-close-requested" })
            }
          />
          <DesktopSettingsModal
            isOpen={isSettingsOpen}
            onClose={(): void => {
              onEvent({ type: "catalog/settings-close-requested" });
            }}
          />
          <button
            ref={desktopThreadCreateButtonRef}
            className="desktop-catalog-fab"
            type="button"
            aria-expanded={isThreadCreateOpen}
            onClick={() =>
              onEvent({
                type: "board/thread-create-toggle-requested",
              })
            }
            onDragEnter={openThreadCreateOnFileDrag}
          >
            {isThreadCreateOpen ? (
              <FiX aria-hidden="true" />
            ) : (
              <FiPlus aria-hidden="true" />
            )}
            {isThreadCreateOpen ? "閉じる" : "新規スレ"}
          </button>
        </BoardQueryState>
      </section>
    );
  }

  return (
    <section aria-labelledby="board-page-title">
      <h1 id="board-page-title" className="sr-only">
        リストモード
      </h1>
      <BoardQueryState
        error={error}
        hasData={data !== undefined}
        isLoading={isLoading}
        isRetrying={isFetching}
        onRetry={(): void => {
          onEvent({ type: "catalog/retry-requested" });
        }}
      >
        <section className="board-page pb-20" aria-label="スレッド一覧">
          <p
            aria-hidden="true"
            className="my-2 text-xl font-bold text-foreground"
          >
            リストモード
          </p>
          {threadList}
          {pagination}
        </section>
        <MobileCatalogFooter
          ariaLabel="リストメニュー"
          isRefreshing={isFetching}
          onOpenSort={(): void => {
            onEvent({ type: "catalog/sort-modal-open-requested" });
          }}
          onOpenTagFilter={(): void => {
            onEvent({ type: "catalog/tag-modal-open-requested" });
          }}
          onRefresh={(): void => {
            onEvent({ type: "catalog/refresh-requested", manual: true });
          }}
          primaryAction={{
            icon: FiPlus,
            label: "新規スレッド",
            onClick: () =>
              onEvent({ type: "catalog/thread-create-open-requested" }),
          }}
        />
        {isSortOpen && (
          <CatalogSortModal
            isOpen
            initialDisplayType="list"
            currentSort={currentSort}
            selectSortMethod={(sort) =>
              onEvent({ type: "catalog/sort-selected", sort })
            }
            onShowCatalog={(): void => {
              onEvent({ type: "board/catalog-requested" });
            }}
            onShowList={(): void => undefined}
            onClose={(): void => {
              onEvent({ type: "catalog/sort-modal-close-requested" });
            }}
          />
        )}
        <TagFilterModal
          isOpen={isTagFilterOpen}
          threads={data?.threads ?? []}
          onClose={(): void => {
            onEvent({ type: "catalog/tag-modal-close-requested" });
          }}
        />
        <ThreadCreateModal
          isOpen={isThreadCreateOpen}
          onClose={() =>
            onEvent({ type: "catalog/thread-create-close-requested" })
          }
        />
      </BoardQueryState>
    </section>
  );
}

interface BoardThreadListProps {
  threads: Thread[];
  tails: Map<string, BoardThreadTail>;
  tailsIsFetching: boolean;
  tailsHasError: boolean;
  openInNewTab: boolean;
  showR18: boolean;
}

export const BoardThreadList: React.FunctionComponent<BoardThreadListProps> = ({
  threads,
  tails,
  tailsIsFetching,
  tailsHasError,
  openInNewTab,
  showR18,
}: BoardThreadListProps) => {
  const threadLinkAttributes = getThreadLinkAttributes(openInNewTab);

  return (
    <>
      {tailsIsFetching && <p aria-live="polite">最新レスを取得中...</p>}
      {tailsHasError && (
        <Message variant="error">
          一部スレッドの最新レスを取得できませんでした
        </Message>
      )}
      {threads.length === 0 ? (
        <Message variant="info">スレッドはありません</Message>
      ) : (
        threads.map((thread) => {
          const isR18Thread = thread.tags.some((tag) =>
            isR18ThreadTagName(tag.name),
          );
          return (
            <article key={thread.id} className="board-thread">
              <CatalogThreadLink
                thread={thread}
                className="board-thread-link"
                {...threadLinkAttributes}
              >
                スレッドを開く（{thread.replyCount}レス）
              </CatalogThreadLink>
              <BoardPost
                post={thread.opPost}
                isOp
                isR18Thread={isR18Thread}
                showR18={showR18}
              />
              {tails.get(thread.id)?.posts.map((post) => (
                <BoardPost
                  key={post.id}
                  post={post}
                  isR18Thread={isR18Thread}
                  showR18={showR18}
                />
              ))}
            </article>
          );
        })
      )}
    </>
  );
};

export function BoardPost(props: BoardPostProps): React.ReactNode {
  const { node, ...view } = useBoardPostPresenter(props);
  return (
    <ComponentScope node={node}>
      <BoardPostView
        {...view}
        onReveal={(identity) => {
          const event = { type: "board-post.reveal", identity };
          node.dispatch(event);
        }}
      />
    </ComponentScope>
  );
}
type BoardPostViewProps = Omit<
  ReturnType<typeof useBoardPostPresenter>,
  "node"
> & { onReveal: (identity: string) => void };
export function BoardPostView({
  isOp,
  displayNo,
  body,
  hasRevealed,
  revealCount,
  attachments,
  onReveal,
}: BoardPostViewProps): React.ReactNode {
  const postContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (revealCount > 0) postContentRef.current?.focus();
  }, [revealCount]);
  return (
    <article className={isOp ? "board-post board-post-op" : "board-post"}>
      {attachments.map((attachment, index) => {
        const attachmentIdentity = attachment.identity;
        const isR18Hidden = attachment.isHidden;
        const orderLabel =
          attachments.length > 1
            ? `（${index + 1}/${attachments.length}）`
            : "";
        return (
          <figure
            key={attachmentIdentity}
            className="board-post-image"
            data-attachment-order={index + 1}
          >
            <img
              src={attachment.src}
              alt={`No.${displayNo}の添付${orderLabel}`}
              loading="lazy"
              decoding="async"
              className={isR18Hidden ? "r18-spoiler-image" : undefined}
            />
            {isR18Hidden && (
              <button
                type="button"
                className="board-r18-spoiler-trigger"
                aria-label={`No.${displayNo}のR18画像${orderLabel}を表示`}
                onClick={(): void => onReveal(attachmentIdentity)}
              />
            )}
          </figure>
        );
      })}
      <div
        ref={postContentRef}
        className="board-post-content"
        tabIndex={hasRevealed ? -1 : undefined}
      >
        <header className="board-post-meta">
          {isOp ? "OP " : ""}No.{displayNo}
        </header>
        <p>{body}</p>
      </div>
    </article>
  );
}
